import { RegistrationMetadata } from '@core/domain/models/reviewItemMetadata/registrationMetadata'
import axios from 'axios'

type ClinicalTrialsResult = {
  id: string
  trialId: string
  lastUpdated?: string
  secondaryIds?: string[]
  publicTitle?: string
  scientificTitle?: string
  url?: string
  publicContactFirstname?: string
  publicContactLastname?: string
  publicContactEmail?: string
  publicContactTel?: string
  publicContactAffiliation?: string
  scientificContactFirstname?: string
  scientificContactLastname?: string
  scientificContactEmail?: string
  scientificContactTel?: string
  scientificContactAffiliation?: string
  studyType?: string
  studyDesign?: string
  phase?: string
  dateRegistration?: string
  dateEnrollment?: string
  targetSize?: number
  recruitmentStatus?: string
  primarySponsor?: string
  secondarySponsors?: string
  sourceSupport?: string
  countries?: string[]
  conditions?: string[]
  interventions?: string[]
  ageMin?: string
  ageMax?: string
  gender?: string
  inclusionCriteria?: string
  primaryOutcome?: string
  secondaryOutcomes?: string
  bridgingFlag?: string
  bridgedType?: string
  childs?: string
  retrospectiveFlag?: number
  resultsUrlLink?: string
  resultsDatePosted?: string
  resultsYesNo?: string
}

export class ClinicalTrialsApiClient {
  private readonly baseUrl =
    location.protocol + '//' + location.host + '/clinical-trials'

  async search({
    query,
    pagination = {
      limit: 5000,
      offset: 0,
    },
    filters,
  }: {
    query: string
    pagination?: { limit: number; offset: number }
    filters?: { startDate?: string; endDate?: string }
  }): Promise<{ results: RegistrationMetadata[]; totalFound: number }> {
    if (!query) return { results: [], totalFound: 0 }

    const { results, totalFound } = await this.findRegistrationInformation({
      query,
      pagination,
      filters,
    })

    return {
      results: this.mapResultsToRegistrationMetadata(results),
      totalFound,
    }
  }

  private async findRegistrationInformation({
    pagination,
    query,
    filters,
  }: {
    query: string
    pagination: { limit: number; offset: number }
    filters?: { startDate?: string; endDate?: string }
  }): Promise<{ results: ClinicalTrialsResult[]; totalFound: number }> {
    try {
      const solrQuery = `(publicTitle:(${encodeURIComponent(query)})) OR (scientificTitle:(${encodeURIComponent(query)})) OR (inclusionCriteria:(${encodeURIComponent(query)})) OR (primaryOutcome:(${encodeURIComponent(query)})) OR (conditions:(${encodeURIComponent(query)})) OR (secondaryOutcomes:(${encodeURIComponent(query)}))`
      let solrUrl = `${this.baseUrl}/?q=${solrQuery}&start=${pagination.offset}&rows=${pagination.limit}&wt=json`
      if (filters?.startDate && filters?.endDate) {
        const startDateValue = new Date(filters.startDate).toISOString()
        const endDateValue = new Date(filters.endDate).toISOString()
        solrUrl += `&fq=dateRegistration:[${startDateValue} TO ${endDateValue}]`
      } else if (filters?.startDate) {
        const date = new Date(filters.startDate).toISOString()
        solrUrl += `&fq=dateRegistration:[${date} TO *]`
      } else if (filters?.endDate) {
        const date = new Date(filters.endDate).toISOString()
        solrUrl += `&fq=dateRegistration:[* TO ${date}]`
      }
      const { data } = await axios.get(solrUrl)
      return {
        results: data.results as ClinicalTrialsResult[],
        totalFound: data.total,
      }
    } catch (error) {
      console.error('Error searching documents:', error)
      return {
        results: [],
        totalFound: 0,
      }
    }
  }

  private mapResultsToRegistrationMetadata(
    results: ClinicalTrialsResult[],
  ): RegistrationMetadata[] {
    return results.map(
      (result) =>
        new RegistrationMetadata({
          registrationId: result.trialId,
          title: result.scientificTitle || result.publicTitle,
          registrationDate: result.dateRegistration,
          url: result.url,
        }),
    )
  }
}
